import { supabase, isSupabaseReady } from '@/integrations/supabase/client';
import { ContactDetailsFormData, FormData } from '@/types/forms';
import { canonicalizeProductSlug } from '@/utils/productSlugs';

export interface SubmissionData {
  productId: string;
  productSlug: string;
  formData: FormData | Record<string, unknown>;
  contactDetails: ContactDetailsFormData;
  tenantId?: string;
}

export interface LeadData {
  licence_location?: string;
  physical_property?: string;
  client_visits?: string;
  cyber_concerns?: string;
  professional_advice?: string;
  recommended_products?: string[];
  tenant_id?: string;
}

/**
 * Service for handling form submissions and lead creation
 */
export class SubmissionService {

  // Normalize slugs via central canonicalizer to match DB slugs
  static normalizeSlug(slug?: string): string {
    try {
      return canonicalizeProductSlug(slug);
    } catch {
      return (slug || '').toLowerCase().replace(/-/g, '_');
    }
  }

  /**
   * Create a lead with contact information
   */
  static async createLead(leadData: LeadData, contactData: ContactDetailsFormData) {
    try {
      // Ensure tenant_id is always set
      const tenantId = leadData.tenant_id || this.getCurrentTenantId();

      // Insert lead
      const { data: lead, error: leadError } = isSupabaseReady
        ? await supabase
            .from('leads')
            .insert({
              licence_location: leadData.licence_location,
              physical_property: leadData.physical_property,
              client_visits: leadData.client_visits,
              cyber_concerns: leadData.cyber_concerns,
              professional_advice: leadData.professional_advice,
              recommended_products: leadData.recommended_products || [],
              tenant_id: tenantId,
              created_at: new Date().toISOString()
            })
            .select()
            .single()
        : { data: { id: 'dev-lead', tenant_id: tenantId }, error: null } as any;

      if (leadError) {
        console.error('Error creating lead:', leadError);
        throw new Error(`Failed to create lead: ${leadError.message}`);
      }

      // Insert contact information
      const { data: contact, error: contactError } = isSupabaseReady
        ? await supabase
            .from('lead_contacts')
            .insert({
              lead_id: lead.id,
              name: contactData.name,
              email: contactData.email,
              phone: contactData.phone,
              tenant_id: tenantId
            })
            .select()
            .single()
        : { data: { id: 'dev-contact', lead_id: lead.id }, error: null } as any;

      if (contactError) {
        console.error('Error creating contact:', contactError);
        throw new Error(`Failed to create contact: ${contactError.message}`);
      }

      return { lead, contact };
    } catch (error) {
      console.error('Error in createLead:', error);
      throw error;
    }
  }

  /**
   * Create a submission for a specific product
   */
  static async createSubmission(submissionData: SubmissionData) {
    try {
      // Normalize slug to match DB convention (underscores)
      const normalizedSlug = this.normalizeSlug(submissionData.productSlug);
      console.log('🔧 Normalized product slug:', { input: submissionData.productSlug, normalized: normalizedSlug });

      console.log('🔍 Starting submission process...', { productSlug: submissionData.productSlug });

      // Ensure tenant_id is always set
      const tenantId = submissionData.tenantId || this.getCurrentTenantId();
      const effectiveTenantId = isSupabaseReady ? tenantId : this.ensureTenantId(tenantId);
      console.log('🏢 Using tenant ID:', effectiveTenantId, '(raw:', tenantId, ', ready:', isSupabaseReady, ')');

      // Resolve product by slug
      let product: { id: string; name: string } | null = null;

      if (!isSupabaseReady) {
        // Local/dev fallback when Supabase client is not configured
        product = { id: 'dev-product', name: submissionData.productSlug };
      } else {
        // Try several slug variants so we don't depend on exact formatting
        const slugCandidates = Array.from(new Set([
          normalizedSlug,
          (submissionData.productSlug || '').toLowerCase(),
          (submissionData.productSlug || '').toLowerCase().replace(/_/g, '-'),
          normalizedSlug.replace(/_/g, '-'),
        ])).filter(Boolean);

        console.log('🔍 Looking up product with candidates:', {
          slugCandidates,
          original: submissionData.productSlug,
          normalized: normalizedSlug,
          tenantId: effectiveTenantId,
        });

        let lastError: unknown = null;

        // 1) Try current tenant first
        for (const candidate of slugCandidates) {
          const { data, error } = await supabase
            .from('products')
            .select('id, name')
            .eq('slug', candidate)
            .eq('tenant_id', effectiveTenantId)
            .maybeSingle();

          if (!error && data) {
            product = data;
            console.log('✅ Found product in tenant with slug candidate:', candidate, product);
            break;
          }

          if (error) {
            lastError = error;
          }
        }

        // 2) Fallback to default tenant if not found
        if (!product) {
          const defaultTenantId = '00000000-0000-0000-0000-000000000001';
          console.log('⚠️ Product not found for tenant. Trying default tenant with candidates:', slugCandidates);

          for (const candidate of slugCandidates) {
            const { data, error } = await supabase
              .from('products')
              .select('id, name')
              .eq('slug', candidate)
              .eq('tenant_id', defaultTenantId)
              .maybeSingle();

            if (!error && data) {
              product = data;
              console.log('✅ Found product in default tenant with slug candidate:', candidate, product);
              break;
            }

            if (error) {
              lastError = error;
            }
          }
        }

        if (!product) {
          console.error('❌ Product lookup failed for all candidates:', slugCandidates, {
            tenantId: effectiveTenantId,
            original: submissionData.productSlug,
            normalized: normalizedSlug,
            lastError,
          });
          throw new Error(`Product not found: ${submissionData.productSlug}`);
        }
      }

      console.log('✅ Found product:', product);

      // Create lead first (if not from decision matrix)
      let leadId = submissionData.formData.leadId;

      if (!leadId) {
        console.log('🔍 Creating new lead...');
        const leadResult = await this.createLead({
          licence_location: null,
          physical_property: null,
          client_visits: null,
          cyber_concerns: null,
          professional_advice: null,
          recommended_products: [submissionData.productSlug],
          tenant_id: tenantId
        }, submissionData.contactDetails);
        leadId = leadResult.lead.id;
        console.log('✅ Created lead with ID:', leadId);
      } else {
        console.log('✅ Using existing lead ID:', leadId);
      }

      // Combine all form data including contact details
      const answersJson = {
        ...submissionData.formData,
        name: submissionData.contactDetails.name,
        email: submissionData.contactDetails.email,
        phone: submissionData.contactDetails.phone
      };

      // Fire-and-forget email notification (best-effort)
      try {
        const notifyPayload = {
          leadId,
          contactName: submissionData.contactDetails.name,
          contactEmail: submissionData.contactDetails.email,
          contactPhone: submissionData.contactDetails.phone,
          productType: submissionData.productSlug,
          tenantSubdomain: undefined as string | undefined,
        };
        fetch('/.netlify/functions/notify-lead', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(notifyPayload),
        }).catch(() => {});
      } catch {}

      // Insert submission
      console.log('🔍 Creating submission...', { leadId, productId: product.id, tenantId });
      const { data: submission, error: submissionError } = isSupabaseReady
        ? await supabase
            .from('submissions')
            .insert({
              lead_id: leadId,
              product_id: product.id,
              answers_json: answersJson,
              tenant_id: effectiveTenantId,
              submitted_at: new Date().toISOString()
            })
            .select()
            .single()
        : { data: { id: 'dev-submission', lead_id: leadId, product_id: product.id }, error: null } as any;

      if (submissionError) {
        console.error('❌ Error creating submission:', submissionError);
        throw new Error(`Failed to create submission: ${submissionError.message}`);
      }

      console.log('✅ Created submission:', submission.id);

      return { submission, leadId };
    } catch (error) {
      console.error('Error in createSubmission:', error);
      throw error;
    }
  }

  /**
   * Create multiple submissions for multi-product quotes
   */
  static async createMultiProductSubmission(
    products: Array<{ slug: string; formData: FormData | Record<string, unknown> }>,
    contactDetails: ContactDetailsFormData,
    tenantId?: string
  ) {
    try {
      console.log('🔍 Starting multi-product submission...', {
        productCount: products.length,
        productSlugs: products.map(p => p.slug),
        contactDetails: { name: contactDetails.name, email: contactDetails.email },
        tenantId
      });

      // Validate input data
      if (!products || products.length === 0) {
        throw new Error('No products provided for submission');
      }

      if (!contactDetails || !contactDetails.name || !contactDetails.email) {
        throw new Error('Contact details are incomplete');
      }

      // Ensure tenant_id is always set
      const finalTenantId = isSupabaseReady ? (tenantId || this.getCurrentTenantId()) : this.ensureTenantId(tenantId);
      console.log('🏢 Using tenant ID:', finalTenantId, '(ready:', isSupabaseReady, ')');

      // Create lead first with recommended products
      console.log('🔍 Creating lead with recommended products...');
      const leadResult = await this.createLead({
        licence_location: null,
        physical_property: null,
        client_visits: null,
        cyber_concerns: null,
        professional_advice: null,
        recommended_products: products.map(p => p.slug),
        tenant_id: finalTenantId
      }, contactDetails);

      const leadId = leadResult.lead.id;
      const submissions = [];

      // Create submission for each product
      for (const product of products) {
        const submissionData: SubmissionData = {
          productId: '', // Will be resolved from slug
          productSlug: product.slug,
          formData: { ...product.formData, leadId },
          contactDetails,
          tenantId: finalTenantId
        };

        const result = await this.createSubmission(submissionData);
        submissions.push(result.submission);
      }

      return { lead: leadResult.lead, contact: leadResult.contact, submissions };
    } catch (error) {
      console.error('Error in createMultiProductSubmission:', error);
      throw error;
    }
  }

  /**
   * Create submission from decision matrix results
   */
  static async createDecisionMatrixSubmission(
    decisionMatrixData: Record<string, unknown>,
    contactDetails: ContactDetailsFormData,
    recommendedProducts: string[],
    tenantId?: string
  ) {
    try {
      // Ensure tenant_id is always set
      const finalTenantId = tenantId || this.getCurrentTenantId();

      // Create lead with decision matrix data
      const leadResult = await this.createLead({
        licence_location: decisionMatrixData.licenceLocation,
        physical_property: decisionMatrixData.physicalProperty,
        client_visits: decisionMatrixData.clientVisits,
        cyber_concerns: decisionMatrixData.cyberConcerns,
        professional_advice: decisionMatrixData.professionalAdvice,
        recommended_products: recommendedProducts,
        tenant_id: finalTenantId
      }, contactDetails);

      return leadResult;
    } catch (error) {
      console.error('Error in createDecisionMatrixSubmission:', error);
      throw error;
    }
  }

  /**
   * Get current tenant ID based on subdomain detection
   */

    // Local/dev fallback when Supabase env is not configured
    private static ensureTenantId(tenantId?: string): string {
      if (tenantId && !tenantId.startsWith('dev-')) return tenantId;
      // Resolve from subdomain on localhost
      const host = typeof window !== 'undefined' ? window.location.hostname : '';
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const subFromHost = host.endsWith('.localhost') ? host.split('.')[0] : undefined;
      const subFromPath = path.match(/^(?:\/business)?\/site\/([^/]+)/)?.[1];
      const sub = subFromHost || subFromPath || 'www';
      // In dev we map known tenants to hardcoded IDs to avoid DB lookups when Supabase is not ready
      const devTenantMap: Record<string, string> = {
        audits: 'dev-audits',
        dubaicommercity: 'dev-dubaicommercity',
        www: 'dev-www'
      };
      return tenantId || devTenantMap[sub] || 'dev-www';
    }

  static getCurrentTenantId(): string {
    if (typeof window === 'undefined') {
      return '00000000-0000-0000-0000-000000000001'; // Default for SSR
    }

    const hostname = window.location.hostname;

    // In development, we may run on subdomain.localhost or path-based tenant previews
    // We cannot resolve a UUID synchronously here; callers should pass tenantId from TenantContext
    // Fall back to the default tenant to avoid hardcoded IDs.
    // Check for subdomain.localhost pattern (development)
    if (hostname.endsWith('.localhost')) {
      return '00000000-0000-0000-0000-000000000001';
    }

    // Check for path-based routing (/site/subdomain) used for previews
    const pathMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
    if (pathMatch) {
      return '00000000-0000-0000-0000-000000000001';
    }

    // Default to main YallaCompare site
    return '00000000-0000-0000-0000-000000000001';
  }
}
